import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Tooltip,
} from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import DocumentThumbnail from '../../BankMatching/DocumentThumbnail';

const ROW_HEIGHT = 40;
const OVERSCAN = 12;

// Fixed table layout keeps the columns from shifting while virtualized rows are
// swapped in and out of the DOM. The unsized column absorbs the leftover width.
const COLUMN_WIDTHS = {
  actions: 150,
  transactionDate: 110,
  description: undefined,
  reference1: 130,
  creditAccount: 110,
  debitAccount: 110,
  balance: 120,
  matchNumber: 110,
  document: 90,
  viewMatch: 140,
};

const COLUMN_COUNT = Object.keys(COLUMN_WIDTHS).length;

const isTypingTarget = (target) => {
  if (!target || !target.tagName) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  );
};

// Space activates a focused button; row selection must not steal it.
const isActivatableTarget = (target) =>
  !!target?.closest?.('button, a, [role="button"], [role="checkbox"]');

const isOverlayOpen = () =>
  !!document.querySelector('.MuiDialog-root, .MuiModal-root, .swal2-container');

const TransactionsTable = ({
  transactions,
  formatDate,
  handleEdit,
  handleSortChange,
  sortConfig,
  handleCheckboxChange,
  selectedTransactions,
  handleDeleteExternalRows,
  handleSelectAll,
  handleViewTransactionMatch,
  setSelectedTransactions,
  enableKeyboardNavigation = true,
}) => {
  const { i18n, t } = useTranslation();
  const direction = i18n.dir();
  const scrollRef = useRef(null);

  // Row the keyboard currently sits on, and the anchor a Shift+Arrow range
  // grows from.
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [anchorIndex, setAnchorIndex] = useState(-1);

  const rowVirtualizer = useVirtualizer({
    count: transactions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  const selectedSet = useMemo(() => new Set(selectedTransactions), [selectedTransactions]);

  const isMatchedTransaction = (transaction) => {
    const matchNumber = transaction?.matchNumber ?? transaction?.MatchNumber;
    return matchNumber != null && matchNumber !== -1 && matchNumber !== 0;
  };

  const selectableTransactions = useMemo(
    () => transactions.filter((tx) => tx.matchNumber === -1),
    [transactions]
  );

  const allSelected =
    selectableTransactions.length > 0 &&
    selectableTransactions.every((tx) => selectedSet.has(tx.id));

  const someSelected =
    selectableTransactions.some((tx) => selectedSet.has(tx.id)) && !allSelected;

  const handleSelectAllChange = (event) => {
    if (handleSelectAll) {
      handleSelectAll(
        selectableTransactions.map((tx) => tx.id),
        event.target.checked
      );
    }
  };

  // Keep the keyboard cursor inside the list when filtering or sorting changes
  // the row count under it.
  useEffect(() => {
    setFocusedIndex((prev) => (prev >= transactions.length ? transactions.length - 1 : prev));
    setAnchorIndex((prev) => (prev >= transactions.length ? -1 : prev));
  }, [transactions.length]);

  const focusRow = useCallback(
    (index) => {
      setFocusedIndex(index);
      rowVirtualizer.scrollToIndex(index, { align: 'auto' });
    },
    [rowVirtualizer]
  );

  // Ids of the selectable (unmatched) rows between two indexes, inclusive.
  const selectableIdsBetween = useCallback(
    (fromIndex, toIndex) => {
      const [start, end] = fromIndex <= toIndex ? [fromIndex, toIndex] : [toIndex, fromIndex];
      return transactions
        .slice(start, end + 1)
        .filter((tx) => tx.matchNumber === -1)
        .map((tx) => tx.id);
    },
    [transactions]
  );

  // "select" adds the range to the selection, "deselect" removes it.
  const applyRange = useCallback(
    (fromIndex, toIndex, mode) => {
      if (!setSelectedTransactions) return;
      const rangeIds = selectableIdsBetween(fromIndex, toIndex);
      if (rangeIds.length === 0) return;

      setSelectedTransactions((prev) => {
        if (mode === 'deselect') {
          const rangeSet = new Set(rangeIds);
          return prev.filter((id) => !rangeSet.has(id));
        }
        return Array.from(new Set([...prev, ...rangeIds]));
      });
    },
    [selectableIdsBetween, setSelectedTransactions]
  );

  // The mode a Shift gesture anchored on `anchorIdx` should use: an anchor that
  // was explicitly deselected turns the gesture into a deselect sweep.
  const rangeModeFor = useCallback(
    (anchorIdx) => {
      const anchorRow = transactions[anchorIdx];
      return anchorRow && !selectedSet.has(anchorRow.id) ? 'deselect' : 'select';
    },
    [transactions, selectedSet]
  );

  const moveFocus = useCallback(
    (delta, extendSelection) => {
      if (transactions.length === 0) return;
      const nextIndex =
        focusedIndex === -1
          ? delta > 0
            ? 0
            : transactions.length - 1
          : Math.min(transactions.length - 1, Math.max(0, focusedIndex + delta));
      focusRow(nextIndex);

      if (!extendSelection) return;

      // A fresh anchor always selects; only an anchor the user deselected on
      // purpose turns the walk into a deselect sweep.
      const hasAnchor = anchorIndex !== -1;
      const effectiveAnchor = hasAnchor ? anchorIndex : focusedIndex === -1 ? nextIndex : focusedIndex;
      if (!hasAnchor) setAnchorIndex(effectiveAnchor);
      const mode = hasAnchor ? rangeModeFor(effectiveAnchor) : 'select';

      if (mode === 'deselect') {
        applyRange(effectiveAnchor, nextIndex, 'deselect');
        return;
      }

      // Rows the walk just stepped back over leave the range and are cleared
      // again, so the selection always mirrors anchor → focused row.
      const previousIndex = focusedIndex === -1 ? nextIndex : focusedIndex;
      const rangeSet = new Set(selectableIdsBetween(effectiveAnchor, nextIndex));
      const exitedIds = selectableIdsBetween(effectiveAnchor, previousIndex).filter(
        (id) => !rangeSet.has(id)
      );
      if (exitedIds.length > 0 && setSelectedTransactions) {
        const exitedSet = new Set(exitedIds);
        setSelectedTransactions((prev) =>
          Array.from(new Set([...prev.filter((id) => !exitedSet.has(id)), ...rangeSet]))
        );
        return;
      }
      applyRange(effectiveAnchor, nextIndex, 'select');
    },
    [
      transactions.length,
      focusedIndex,
      anchorIndex,
      focusRow,
      applyRange,
      rangeModeFor,
      selectableIdsBetween,
      setSelectedTransactions,
    ]
  );

  const toggleFocusedRow = useCallback(
    (shiftKey) => {
      const row = transactions[focusedIndex];
      if (!row || row.matchNumber !== -1) return;

      if (shiftKey && anchorIndex !== -1 && anchorIndex < transactions.length) {
        applyRange(anchorIndex, focusedIndex, rangeModeFor(anchorIndex));
        return;
      }
      handleCheckboxChange(row);
      setAnchorIndex(focusedIndex);
    },
    [transactions, focusedIndex, anchorIndex, applyRange, rangeModeFor, handleCheckboxChange]
  );

  useEffect(() => {
    if (!enableKeyboardNavigation) return undefined;

    const handleKeyDown = (event) => {
      if (isTypingTarget(event.target) || isOverlayOpen()) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveFocus(1, event.shiftKey);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveFocus(-1, event.shiftKey);
          break;
        case 'PageDown':
          event.preventDefault();
          moveFocus(10, event.shiftKey);
          break;
        case 'PageUp':
          event.preventDefault();
          moveFocus(-10, event.shiftKey);
          break;
        case 'Home':
          event.preventDefault();
          moveFocus(-transactions.length, event.shiftKey);
          break;
        case 'End':
          event.preventDefault();
          moveFocus(transactions.length, event.shiftKey);
          break;
        case ' ':
          if (isActivatableTarget(event.target)) break;
          event.preventDefault();
          // Ignore auto-repeat so holding the key does not flicker the selection.
          if (!event.repeat) toggleFocusedRow(event.shiftKey);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [enableKeyboardNavigation, moveFocus, toggleFocusedRow, transactions.length]);

  const headerStyle = {
    backgroundColor: 'var(--foundation-blue-light-active, #BFC8FF)',
    color: 'var(--Foundation-Blue-Normal, #304FFF)',
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: '14px',
    fontWeight: 'bold',
    height: ROW_HEIGHT,
    maxHeight: ROW_HEIGHT,
    padding: '0 6px',
    cursor: 'pointer',
    boxSizing: 'border-box',
  };

  const bodyCellSx = {
    height: ROW_HEIGHT,
    maxHeight: ROW_HEIGHT,
    padding: '4px 6px',
    py: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const spacerCellSx = { p: 0, border: 0, height: 'inherit' };

  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;

  const dataColumns = [
    { key: 'transactionDate', label: t('date'), type: 'date' },
    { key: 'description', label: t('Description'), type: 'string' },
    { key: 'reference1', label: t('Reference'), type: 'string' },
    // Flipped display: positive amounts (stored as debitAccount) are shown
    // under the Credit (זכות) column, negatives under Debit (חובה).
    // Sort keys stay aligned with the value each column actually shows.
    { key: 'creditAccount', label: t('Debit'), type: 'number' },
    { key: 'debitAccount', label: t('Credit'), type: 'number' },
    { key: 'balance', label: t('Balance'), type: 'number' },
    { key: 'matchNumber', label: t('MatchNumber'), type: 'number' },
  ];

  return (
    <div
      className={`${direction} bank-transactions-table`}
      style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <TableContainer
        component={Paper}
        ref={scrollRef}
        sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}
      >
        <Table aria-label="Transactions Table" stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell
                key={'isChecked'}
                align="center"
                className="table-headers"
                sx={{ ...headerStyle, width: COLUMN_WIDTHS.actions }}
                onClick={() => handleSortChange('isChecked', 'isChecked')}
              >
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                  <Tooltip title={allSelected ? t('DeselectAll') : t('SelectAll')} arrow>
                    <span>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        disabled={selectableTransactions.length === 0}
                        onChange={handleSelectAllChange}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                    </span>
                  </Tooltip>
                  {selectedTransactions.length ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteExternalRows();
                      }}
                      sx={{
                        backgroundColor: '#d32f2f',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#c62828' },
                        fontSize: '0.75rem',
                        minWidth: 0,
                        px: 1,
                      }}
                      startIcon={<DeleteIcon />}
                    >
                      {`${t('Delete')} (${selectedTransactions.length})`}
                    </Button>
                  ) : (
                    <>
                      {t('Actions')}
                      {sortConfig.key === 'isChecked' && (
                        sortConfig.direction === 'ascending' ? (
                          <ArrowUpwardIcon fontSize="small" />
                        ) : (
                          <ArrowDownwardIcon fontSize="small" />
                        )
                      )}
                    </>
                  )}
                </div>
              </TableCell>
              {dataColumns.map((header) => (
                <TableCell
                  key={header.key}
                  align="center"
                  className="table-headers"
                  sx={{ ...headerStyle, width: COLUMN_WIDTHS[header.key] }}
                  onClick={() => handleSortChange(header.key, header.type)}
                >
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                    {header.label}
                    {sortConfig.key === header.key && (
                      sortConfig.direction === 'ascending' ? (
                        <ArrowUpwardIcon fontSize="small" />
                      ) : (
                        <ArrowDownwardIcon fontSize="small" />
                      )
                    )}
                  </div>
                </TableCell>
              ))}
              <TableCell
                align="center"
                className="table-headers"
                sx={{ ...headerStyle, cursor: 'default', width: COLUMN_WIDTHS.document }}
              >
                {t('Document')}
              </TableCell>
              <TableCell
                align="center"
                className="table-headers"
                sx={{ ...headerStyle, cursor: 'default', width: COLUMN_WIDTHS.viewMatch }}
              >
                {t('ViewTransactionMatch')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paddingTop > 0 && (
              <TableRow style={{ height: paddingTop }}>
                <TableCell colSpan={COLUMN_COUNT} sx={spacerCellSx} />
              </TableRow>
            )}
            {virtualRows.map((virtualRow) => {
              const index = virtualRow.index;
              const transaction = transactions[index];
              if (!transaction) return null;
              const isFocused = index === focusedIndex;

              return (
                <Tooltip key={transaction.id ?? index} title={t('DoubleClickToEdit')} arrow>
                  <TableRow
                    sx={{
                      height: ROW_HEIGHT,
                      maxHeight: ROW_HEIGHT,
                      boxShadow: isFocused ? 'inset 3px 0 0 0 #304FFF' : 'none',
                    }}
                    style={{
                      backgroundColor: isFocused
                        ? 'rgba(48, 79, 255, 0.12)'
                        : transaction.matchNumber !== -1
                          ? 'rgba(249, 170, 42, 0.1)'
                          : 'inherit',
                    }}
                    onClick={() => {
                      setFocusedIndex(index);
                      setAnchorIndex(index);
                    }}
                    onDoubleClick={() => handleEdit(transaction)}
                  >
                    <TableCell align="center" sx={bodyCellSx}>
                      <Checkbox
                        disabled={transaction.matchNumber !== -1}
                        checked={selectedSet.has(transaction.id)}
                        onChange={() => handleCheckboxChange(transaction)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusedIndex(index);
                          if (e.shiftKey && anchorIndex !== -1) {
                            applyRange(anchorIndex, index, rangeModeFor(anchorIndex));
                          } else {
                            setAnchorIndex(index);
                          }
                        }}
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      {transaction.transactionDate ? formatDate(transaction.transactionDate) : '-'}
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      <Tooltip title={transaction.description || ''} arrow>
                        <span>{transaction.description || '-'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>{transaction.reference1 || '-'}</TableCell>
                    {/* Debit (חובה) column shows negatives, Credit (זכות) shows positives */}
                    <TableCell align="center" sx={bodyCellSx}>{beautifyNumber(transaction.creditAccount)}</TableCell>
                    <TableCell align="center" sx={bodyCellSx}>{beautifyNumber(transaction.debitAccount)}</TableCell>
                    <TableCell align="center" sx={bodyCellSx}>{beautifyNumber(transaction.balance)}</TableCell>
                    <TableCell align="center" sx={bodyCellSx}>{transaction.matchNumber === -1 ? '-' : transaction.matchNumber}</TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      {transaction.documentUrl ? (
                        <Tooltip title={t('ViewDocument')} arrow>
                          <span>
                            <DocumentThumbnail
                              url={transaction.documentUrl}
                              title={`document-${transaction.id}`}
                            />
                          </span>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      {isMatchedTransaction(transaction) && handleViewTransactionMatch ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTransactionMatch(transaction);
                          }}
                          sx={{
                            fontSize: '0.7rem',
                            py: 0.25,
                            px: 1,
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            borderColor: '#304FFF',
                            color: '#304FFF',
                            '&:hover': {
                              borderColor: '#1E3ACC',
                              backgroundColor: 'rgba(48, 79, 255, 0.06)',
                            },
                          }}
                        >
                          {t('ViewTransactionMatch')}
                        </Button>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                </Tooltip>
              );
            })}
            {paddingBottom > 0 && (
              <TableRow style={{ height: paddingBottom }}>
                <TableCell colSpan={COLUMN_COUNT} sx={spacerCellSx} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="bank-transactions-table__status">
        <span>{`${t('Rows')}: ${transactions.length}`}</span>
        {selectedTransactions.length > 0 && (
          <span>{`${t('Selected')}: ${selectedTransactions.length}`}</span>
        )}
        <span className="bank-transactions-table__hint">{t('BankKeyboardHint')}</span>
      </div>
    </div>
  );
};

export default TransactionsTable;
